import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  documentosAceitos,
  layoutTotem,
  mascaraDocumento,
  rotuloDocumentos,
  tamanhosValidos,
  textoInstrucao,
} from '../api/eventConfig'
import { buscarPessoas, PessoaEncontrada } from '../api/lookup'
import LayoutAmplo from '../components/totem/LayoutAmplo'
import LayoutClassico from '../components/totem/LayoutClassico'
import { ACAO, APAGAR } from '../components/totem/tipos'
import { useEventConfig } from '../hooks/useEventConfig'
import SelecaoPessoa from './SelecaoPessoa'

/** Formata progressivamente como CPF (11) ou CNPJ (14) enquanto se digita. */
function formatarDocumento(d: string): string {
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')
  }
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5')
}

/** Volta à tela inicial quando o participante se afasta sem concluir. */
const INATIVIDADE_MS = 90_000

/**
 * A tela do totem.
 *
 * Aqui fica só o comportamento — digitação, busca, inatividade, teclado físico. A aparência
 * está nos arranjos de `components/totem`, e qual deles aparece é configuração do evento.
 * A separação existe para que trocar o visual não possa alterar a regra de credenciamento.
 */
const Autoatendimento: React.FC = () => {
  const navigate = useNavigate()
  const { config, logo } = useEventConfig()
  const [documento, setDocumento] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [candidatos, setCandidatos] = useState<PessoaEncontrada[] | null>(null)

  // Tipos de documento aceitos vêm do evento: este congresso usa CPF ou CNPJ, mas
  // outros clientes credenciam por RG. A tela se adapta sem alteração de código.
  const tipos = documentosAceitos(config)
  const tamanhos = tamanhosValidos(tipos)
  const rotulo = rotuloDocumentos(tipos)

  const cores = config.branding || {}
  const paleta = {
    primaria: cores.primaryColor || '#092D77',
    secundaria: cores.secondaryColor || '#38B7B4',
    destaque: cores.accentColor || '#235C78',
  }

  const limpar = useCallback(() => {
    setDocumento('')
    setErro(null)
    setCandidatos(null)
    setBuscando(false)
  }, [])

  // Totem desassistido: quem desiste no meio não pode deixar os próprios dados na tela
  // para o próximo participante.
  useEffect(() => {
    if (!documento && !candidatos && !erro) return
    const t = setTimeout(limpar, INATIVIDADE_MS)
    return () => clearTimeout(t)
  }, [documento, candidatos, erro, limpar])

  const seguirPara = useCallback(
    (p: PessoaEncontrada) => navigate('/impressao', { state: { pessoa: p } }),
    [navigate],
  )

  const buscar = useCallback(async () => {
    const limpo = documento.replace(/\D/g, '')
    if (!tamanhos.includes(limpo.length)) {
      setErro(`Digite um ${rotulo} válido.`)
      return
    }
    setBuscando(true)
    setErro(null)
    try {
      const r = await buscarPessoas(limpo)
      if (r.total === 0) {
        setErro('Não encontramos esse documento. Procure a secretaria do evento.')
      } else if (r.requerSelecao) {
        // Vários participantes sob o mesmo documento — típico de inscrição feita pela
        // empresa. Escolher sozinho entregaria o crachá, e o QR, de outra pessoa.
        setCandidatos(r.resultados)
      } else {
        seguirPara(r.resultados[0])
      }
    } catch {
      setErro('Não foi possível consultar agora. Tente novamente ou procure a secretaria.')
    } finally {
      setBuscando(false)
    }
  }, [documento, seguirPara, tamanhos, rotulo])

  const tecla = useCallback(
    (t: string) => {
      setErro(null)
      if (t === APAGAR) return setDocumento(d => d.slice(0, -1))
      if (t === ACAO) return void buscar()
      setDocumento(d => (d.length < 14 ? d + t : d))
    },
    [buscar],
  )

  // O briefing prevê o mesmo sistema em notebook, onde se digita pelo teclado físico.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) tecla(e.key)
      else if (e.key === 'Backspace') tecla(APAGAR)
      else if (e.key === 'Enter') tecla(ACAO)
      else if (e.key === 'Escape') limpar()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tecla, limpar])

  if (candidatos) {
    return (
      <SelecaoPessoa
        pessoas={candidatos}
        documento={formatarDocumento(documento)}
        onEscolher={seguirPara}
        onCancelar={limpar}
      />
    )
  }

  const props = {
    logo,
    nomeEvento: config.name,
    rotulo,
    instrucao: textoInstrucao(config, rotulo),
    documento: documento ? formatarDocumento(documento) : '',
    mascara: mascaraDocumento(tipos),
    erro,
    buscando,
    onTecla: tecla,
    cores: paleta,
  }

  return layoutTotem(config) === 'amplo' ? <LayoutAmplo {...props} /> : <LayoutClassico {...props} />
}

export default Autoatendimento
